import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Send, ArrowLeft, ImagePlus, X, Lock, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { useMessageEncryption } from '@/hooks/useMessageEncryption';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import WysiwygEditor from '@/components/WysiwygEditor';

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  subject?: string;
  content?: string;
  message?: string;
  image_url?: string;
  created_at: string;
  _optimistic?: boolean;
}

interface ConversationViewProps {
  otherUserId: string;
  otherUserName: string;
  initialSubject: string;
  onBack: () => void;
  orderId?: string;
  /** Admin: skip chat_authorized check */
  bypassAuth?: boolean;
  /** Use WYSIWYG rich text editor for composing messages */
  richText?: boolean;
}

/** Strip HTML tags to check if Quill content is truly empty */
const isRichTextEmpty = (html: string) => {
  return !html.replace(/<[^>]*>/g, '').trim();
};

/** Detect if a message string contains HTML */
const isHtml = (str: string) => /<[a-z][\s\S]*>/i.test(str);

export const ConversationView = ({
  otherUserId,
  otherUserName,
  initialSubject,
  onBack,
  orderId,
  bypassAuth = false,
  richText = false,
}: ConversationViewProps) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { sendEncryptedMessage, decryptMessages } = useMessageEncryption();
  const [messages, setMessages] = useState<Message[]>([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [chatAuthorized, setChatAuthorized] = useState<boolean | null>(null);
  const [chatRequested, setChatRequested] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [requestingChat, setRequestingChat] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check chat authorization
  useEffect(() => {
    const checkChatAuthorization = async () => {
      // Admin bypass: always allow
      if (bypassAuth) {
        setChatAuthorized(true);
        setCheckingAuth(false);
        return;
      }

      if (!orderId) {
        // No order context = allow chat (e.g., relay contact)
        setChatAuthorized(true);
        setCheckingAuth(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('orders')
          .select('chat_authorized')
          .eq('id', orderId)
          .single();

        if (error) {
          // Column may not exist yet — allow chat as fallback
          console.warn('chat_authorized check failed:', error.message);
          setChatAuthorized(true);
        } else {
          setChatAuthorized((data as any)?.chat_authorized ?? false);
          setChatRequested(!!(data as any)?.chat_requested_at);
        }
      } catch {
        setChatAuthorized(true);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkChatAuthorization();
  }, [orderId]);

  // Fetch messages + subscribe to realtime
  useEffect(() => {
    if (user?.id) {
      fetchConversation();

      const channel = supabase
        .channel(`conversation-${user.id}-${otherUserId}-${orderId ?? 'no-order'}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `recipient_id=eq.${user.id}`
          },
          (payload) => {
            const newMsg = payload.new as Message;
            setMessages(prev => {
              // Replace optimistic message if timestamps match
              const optimisticIdx = prev.findIndex(m => m._optimistic);
              if (optimisticIdx >= 0) {
                const updated = [...prev];
                updated[optimisticIdx] = { ...newMsg, _optimistic: false };
                return updated;
              }
              // Skip duplicates
              if (prev.some(m => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });
            scrollToBottom();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `sender_id=eq.${user.id}`
          },
          (payload) => {
            const newMsg = payload.new as Message;
            setMessages(prev => {
              // Replace optimistic message with real one
              const optimisticIdx = prev.findIndex(m => m._optimistic);
              if (optimisticIdx >= 0) {
                const updated = [...prev];
                updated[optimisticIdx] = { ...newMsg, _optimistic: false };
                return updated;
              }
              if (prev.some(m => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });
            scrollToBottom();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user?.id, otherUserId, orderId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 50);
  };

  const fetchConversation = async () => {
    if (!user?.id) return;

    try {
      const base = supabase
        .from('messages')
        .select('*')
        .or(
          `and(sender_id.eq.${user.id},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${user.id})`
        );

      let q: any = base;
      if (orderId) {
        q = q.eq('order_id', orderId);
      }

      const { data, error } = await q.order('created_at', { ascending: true });

      if (error) throw error;

      const decryptedMessages = data ? await decryptMessages(data as any) : [];
      setMessages(decryptedMessages as any);
      scrollToBottom();
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  // Image handling
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5 Mo)');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont acceptées');
      return;
    }

    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!user?.id) return null;

    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const { error } = await supabase.storage
      .from('chat-images')
      .upload(fileName, file, {
        contentType: file.type,
        upsert: false
      });

    if (error) {
      console.error('Image upload failed:', error);
      toast.error("Erreur lors de l'envoi de l'image");
      return null;
    }

    const { data: publicUrl } = supabase.storage
      .from('chat-images')
      .getPublicUrl(fileName);

    return publicUrl.publicUrl;
  };

  const handleSendMessage = async () => {
    const isEmpty = richText ? isRichTextEmpty(replyText) : !replyText.trim();
    if ((isEmpty && !selectedImage) || !user?.id) return;
    if (!chatAuthorized) {
      toast.error("Le chat n'est pas encore autorisé par un administrateur");
      return;
    }

    const messageText = richText ? (isRichTextEmpty(replyText) ? '' : replyText) : replyText.trim();
    const imageFile = selectedImage;
    const currentPreview = imagePreview;

    // Clear inputs immediately for instant feel
    setReplyText('');
    removeSelectedImage();
    setSending(true);

    // Optimistic message — appears instantly
    const optimisticMsg: Message = {
      id: `temp-${Date.now()}`,
      sender_id: user.id,
      recipient_id: otherUserId,
      content: messageText || (imageFile ? '📷 Image' : ''),
      created_at: new Date().toISOString(),
      _optimistic: true,
    };

    if (imageFile && currentPreview) {
      optimisticMsg.image_url = currentPreview;
    }

    setMessages(prev => [...prev, optimisticMsg]);
    scrollToBottom();

    try {
      let imageUrl: string | null = null;

      // Upload image if selected
      if (imageFile) {
        setUploadingImage(true);
        imageUrl = await uploadImage(imageFile);
        setUploadingImage(false);

        if (!imageUrl && !messageText) {
          // Image upload failed and no text — remove optimistic msg
          setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
          setSending(false);
          return;
        }
      }

      // Build the message content: include image URL in content if image_url column fails
      const contentToSend = messageText || (imageUrl ? '📷 Image' : '');

      const { error } = await sendEncryptedMessage({
        recipientId: otherUserId,
        subject: messages.length > 0 ? (messages[0].subject || initialSubject) : initialSubject,
        message: contentToSend,
        orderId: orderId,
        imageUrl: imageUrl || undefined,
      });

      if (error) throw error;

      // Send email notification (non-blocking)
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();

        supabase.functions.invoke('send-message-notification', {
          body: {
            recipientId: otherUserId,
            senderName: profile?.full_name || 'Un utilisateur',
            subject: messages.length > 0 ? (messages[0].subject || initialSubject) : initialSubject,
            message: contentToSend || '📷 Image envoyée',
            orderId: orderId
          }
        }).catch(() => {});
      } catch {}

      // Refetch to get the real message from DB and replace optimistic
      fetchConversation();
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error(t('conversationView.sendError'));
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
    } finally {
      setSending(false);
    }
  };

  const handleRequestChat = async () => {
    if (!user?.id || !orderId) return;
    setRequestingChat(true);

    try {
      const { error } = await supabase
        .from('orders')
        .update({
          chat_requested_at: new Date().toISOString(),
          chat_requested_by: user.id,
        } as any)
        .eq('id', orderId);

      if (error) throw error;

      setChatRequested(true);
      toast.success('Demande de chat envoyée ! Un administrateur examinera votre demande.');
    } catch (error: any) {
      console.error('Error requesting chat:', error);
      toast.error("Impossible d'envoyer la demande");
    } finally {
      setRequestingChat(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getMessageContent = (msg: Message) => msg.content || msg.message || '';

  const isImageUrl = (url: string) => {
    return url && (
      url.includes('/chat-images/') ||
      url.startsWith('data:image/') ||
      /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url)
    );
  };

  // Loading state
  if (checkingAuth) {
    return (
      <div className="flex items-center justify-center h-full py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-card">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3 flex-1">
          <Avatar>
            <AvatarFallback>
              {otherUserName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-semibold">{otherUserName}</h3>
            <p className="text-sm text-muted-foreground">
              {messages.length > 0 ? (messages[0].subject || initialSubject) : initialSubject}
            </p>
          </div>
        </div>
      </div>

      {/* Chat not authorized banner */}
      {!chatAuthorized && (
        <div className="p-4 bg-amber-50 border-b border-amber-200 flex items-center gap-3">
          <Lock className="h-5 w-5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-900">Chat non autorisé</p>
            <p className="text-xs text-amber-700">
              Un administrateur doit autoriser le chat pour cette commande avant que vous puissiez envoyer des messages.
            </p>
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {chatAuthorized
                  ? t('conversationView.noMessages')
                  : "En attente d'autorisation admin pour démarrer la conversation."
                }
              </CardContent>
            </Card>
          ) : (
            messages.map((msg) => {
              const isCurrentUser = msg.sender_id === user?.id;
              const msgContent = getMessageContent(msg);

              return (
                <div
                  key={msg.id}
                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex gap-2 max-w-[75%] ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'}`}>
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarFallback className="text-xs">
                        {(isCurrentUser ? t('conversationView.you') : otherUserName).substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`space-y-1 ${isCurrentUser ? 'items-end' : 'items-start'} flex flex-col`}>
                      <div
                        className={`rounded-lg px-4 py-2 ${
                          isCurrentUser
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        } ${msg._optimistic ? 'opacity-70' : ''}`}
                      >
                        {/* Image */}
                        {msg.image_url && isImageUrl(msg.image_url) && (
                          <div className="mb-2">
                            <img
                              src={msg.image_url}
                              alt="Image partagée"
                              className="max-w-[280px] max-h-[280px] rounded-md object-cover cursor-pointer"
                              onClick={() => window.open(msg.image_url!, '_blank')}
                              loading="lazy"
                            />
                          </div>
                        )}
                        {/* Text: render HTML safely if rich text, otherwise plain */}
                        {msgContent && msgContent !== '📷 Image' && (
                          isHtml(msgContent) ? (
                            <div
                              className="text-sm prose prose-sm max-w-none [&_a]:underline [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(msgContent, { ALLOWED_TAGS: ['p','br','b','i','u','s','strong','em','a','ul','ol','li','h1','h2','h3','blockquote','code','pre','span'], ALLOWED_ATTR: ['href','target','rel','class'] })
                              }}
                            />
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">{msgContent}</p>
                          )
                        )}
                        {msg._optimistic && uploadingImage && (
                          <div className="flex items-center gap-1 mt-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span className="text-xs opacity-70">Envoi...</span>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(msg.created_at).toLocaleString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Image Preview */}
      {imagePreview && (
        <div className="px-4 pt-2 border-t bg-card">
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt="Aperçu"
              className="h-20 w-20 rounded-md object-cover border"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full"
              onClick={removeSelectedImage}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t bg-card">
        {chatAuthorized ? (
          <>
            {richText ? (
              /* WYSIWYG mode */
              <div className="space-y-2">
                <WysiwygEditor
                  value={replyText}
                  onChange={setReplyText}
                  placeholder={t('conversationView.writePlaceholder')}
                  className="min-h-[120px]"
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sending}
                    >
                      <ImagePlus className="h-4 w-4 mr-1" />
                      Image
                    </Button>
                  </div>
                  <Button
                    onClick={handleSendMessage}
                    disabled={(isRichTextEmpty(replyText) && !selectedImage) || sending}
                    size="sm"
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Send className="h-4 w-4 mr-1" />
                    )}
                    Envoyer
                  </Button>
                </div>
              </div>
            ) : (
              /* Plain text mode */
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  className="shrink-0"
                  disabled={sending}
                >
                  <ImagePlus className="h-5 w-5 text-muted-foreground" />
                </Button>
                <Textarea
                  placeholder={t('conversationView.writePlaceholder')}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  rows={2}
                  className="resize-none"
                  disabled={sending}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={(!replyText.trim() && !selectedImage) || sending}
                  size="icon"
                  className="shrink-0 h-auto"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
            {!richText && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('conversationView.sendHint')}
              </p>
            )}
          </>
        ) : (
          <div className="text-center py-3 space-y-2">
            <div className="text-sm text-muted-foreground">
              <Lock className="h-4 w-4 inline mr-1" />
              {chatRequested
                ? 'Demande envoyée — en attente d\'autorisation administrateur'
                : 'Chat verrouillé — demandez l\'autorisation pour communiquer'
              }
            </div>
            {!chatRequested && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRequestChat}
                disabled={requestingChat}
              >
                {requestingChat ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Demander l'ouverture du chat
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
